import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Tooltip,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  BackButton, CopyButton, TableAccordionInfinite, TableAccordionColumn,
} from '@marageti/z4-lib';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { User } from '@marageti/z4-sdk/lib/auth';
import AuditFilters from '../audit-filters';
import { useUserCache } from './use-user-cache';
import UserDataCard from './user-data-card';
import { ApiEvent } from '../types';
import { LOG_LEVELS } from '../constants';
import { formatDateSafe } from '../audit-utils';

const getStatusColor = (status: number): 'success' | 'warning' | 'error' | 'default' => {
  if (status >= 200 && status < 300) return 'success';
  if (status >= 300) return 'error';
  return 'warning';
};

const formatTruncatedText = (value: unknown, maxWidth = 200): React.ReactNode => {
  const text = String(value ?? '');
  return (
    <Tooltip title={text} placement="top-start">
      <Box
        sx={{
          maxWidth,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
};

const formatUserCell = (
  value: unknown,
  userCache: UserCacheActions,
): React.ReactNode => {
  const userId = String(value ?? '');
  if (!userId) return '-';
  const user = userCache.getUserData(userId);
  const loading = userCache.isLoadingUser(userId);

  // Loading
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CircularProgress size={14} />
      </Box>
    );
  }

  // Data loaded -- show email + roles
  if (user) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" noWrap>
          {user.email}
        </Typography>
      </Box>
    );
  }

  // Error or not found -- show retry button
  if (userCache.hasError(userId) || user === null) {
    return (
      <Box display="flex" alignItems="center" gap={0.5}>
        <Button
          variant="text"
          size="small"
          startIcon={<PersonOffIcon sx={{ fontSize: 16, color: 'error.main' }} />}
          onClick={(e) => {
            e.stopPropagation();
            userCache.fetchUser(userId);
          }}
          sx={{
            textTransform: 'none', whiteSpace: 'nowrap', fontSize: '12px', fontWeight: 400,
          }}
        >
          Retry user information
        </Button>
      </Box>
    );
  }

  // Not yet fetched -- show search button with text
  return (
    <Button
      variant="text"
      size="small"
      startIcon={<PersonSearchIcon sx={{ fontSize: 16, color: 'common.black50' }} />}
      onClick={(e) => {
        e.stopPropagation();
        userCache.fetchUser(userId);
      }}
      sx={{
        textTransform: 'none', whiteSpace: 'nowrap', fontSize: '12px', fontWeight: 400,
      }}
    >
      Get user information
    </Button>
  );
};

interface UserCacheActions {
  fetchUser: (userId: string) => void;
  getUserData: (userId: string) => User | null | undefined;
  isLoadingUser: (userId: string) => boolean;
  hasError: (userId: string) => boolean;
}

const METHOD_ICONS: Record<string, React.ReactNode> = {
  GET: <VisibilityIcon sx={{ fontSize: 16 }} />,
  POST: <AddCircleOutlineIcon sx={{ fontSize: 16 }} />,
  PUT: <FileUploadIcon sx={{ fontSize: 16 }} />,
  PATCH: <EditNoteIcon sx={{ fontSize: 16 }} />,
  DELETE: <DeleteOutlineIcon sx={{ fontSize: 16 }} />,
};

const buildColumns = (userCache: UserCacheActions): TableAccordionColumn<ApiEvent>[] => [
  {
    fieldPath: 'httpMethod',
    label: 'Method',
    style: { width: '100px' },
    format: (value) => {
      const method = String(value ?? '').toUpperCase();
      const icon = METHOD_ICONS[method] || <HelpOutlineIcon sx={{ fontSize: 16 }} />;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {icon}
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {method}
          </Typography>
        </Box>
      );
    },
  },
  {
    fieldPath: 'logLevel',
    label: 'Level',
    style: { width: '80px' },
    format: (value) => (
      <Chip
        label={String(value)}
        color={value === 'INFO' ? 'default' : 'error'}
        size="small"
        variant="outlined"
      />
    ),
  },
  {
    fieldPath: 'resourceUri',
    label: 'Resource URI',
    format: (value) => formatTruncatedText(value),
  },
  {
    fieldPath: 'entityId',
    label: 'Entity ID',
    format: (value) => {
      const text = String(value ?? '-');
      if (!value) return text;
      return (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <>
            <Typography variant="body2" noWrap>
              {text}
            </Typography>
            <CopyButton
              textToCopy={text}
              size="small"
              iconFontSize="small"
              sx={{ p: 0.25 }}
            />
          </>
        </Stack>
      );
    },
  },
  {
    fieldPath: 'httpStatus',
    label: 'Status',
    style: { width: '70px' },
    format: (value) => (
      <Chip
        label={String(value)}
        color={getStatusColor(Number(value))}
        size="small"
      />
    ),
  },
  {
    fieldPath: 'userId',
    label: 'User',
    format: (value) => formatUserCell(value, userCache),
  },
  {
    fieldPath: 'createdAt',
    label: 'Created',
    style: { width: '202px', maxWidth: '202px', minWidth: '202px' },
    format: (value) => formatDateSafe(value),
  },
  {
    fieldPath: 'id',
    label: 'ID',
    isInAccordion: true,
  },
  {
    fieldPath: 'userId',
    label: 'User ID',
    isInAccordion: true,
    format: (_value, row) => row?.userId ?? '-',
  },
  {
    fieldPath: 'superUserId',
    label: 'Super User ID',
    isInAccordion: true,
  },
  {
    fieldPath: 'displayMessage',
    label: 'Message',
    isInAccordion: true,
    format: (value) => formatTruncatedText(value),
  },
  {
    fieldPath: 'message',
    label: 'Full Message',
    isInAccordion: true,
    format: (value) => {
      try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        return (
          <pre style={{ fontSize: '12px', whiteSpace: 'pre-wrap', margin: 0 }}>
            {JSON.stringify(parsed, null, 2)}
          </pre>
        );
      } catch {
        return (
          <pre style={{ fontSize: '12px', whiteSpace: 'pre-wrap', margin: 0 }}>
            {String(value)}
          </pre>
        );
      }
    },
  },
  {
    fieldPath: 'expiresAt',
    label: 'Expires At (TTL)',
    isInAccordion: true,
    format: (value) => formatDateSafe(value),
  },
];

interface EventsViewProps {
  events: ApiEvent[];
  isLoading: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => void;
  filters: {
    day: string;
    entity: string;
    logLevel: string;
  };
  onFilterChange: (name: string, value: unknown) => void;
}

const EventsView: React.FC<EventsViewProps> = ({
  events,
  isLoading,
  error,
  hasMore,
  loadMore,
  filters,
  onFilterChange,
}) => {
  const navigate = useNavigate();
  const userCache = useUserCache();
  const { getUserData, isLoadingUser, hasError: hasUserError } = userCache;
  const columns = useMemo(() => buildColumns(userCache), [userCache]);

  const filterFields = [
    {
      name: 'day',
      label: 'Day',
      type: 'date' as const,
      value: filters.day,
      onChange: (value: unknown) => onFilterChange('day', value),
    },
    {
      name: 'entity',
      label: 'Entity ID',
      type: 'text' as const,
      value: filters.entity,
      onChange: (value: unknown) => onFilterChange('entity', value),
    },
    {
      name: 'logLevel',
      label: 'Log Level',
      type: 'select' as const,
      options: LOG_LEVELS,
      value: filters.logLevel,
      onChange: (value: unknown) => onFilterChange('logLevel', value),
    },
  ];

  return (
    <Box>
      <Stack alignItems="center" justifyContent="flex-start" flexDirection="row" gap={1} sx={{ mb: 2 }}>
        <BackButton onClick={() => navigate('/settings/audit')} />
        <Typography variant="h5">
          API Events
        </Typography>
      </Stack>

      <AuditFilters
        fields={filterFields}
        error={error}
        total={`${events.length} events loaded`}
      />

      <TableAccordionInfinite<ApiEvent>
        columns={columns}
        data={events}
        getRowId={(event) => event.id}
        isLoading={isLoading}
        hasMore={hasMore}
        loadMore={loadMore}
        emptyMessage="No events found. Try adjusting your filters."
        isEmpty={events.length === 0}
        renderExtraContent={(event) => {
          const userData = getUserData(event.userId);
          const loadingUser = isLoadingUser(event.userId);
          const userError = hasUserError(event.userId);
          if (userData === undefined && !loadingUser) return undefined;
          return (
            <UserDataCard
              user={userData}
              isLoading={loadingUser}
              hasError={userError}
            />
          );
        }}
      />
    </Box>
  );
};

export default EventsView;
